import React from 'react';
import { Zap, Sparkles, FileText, TrendingUp } from 'lucide-react';

interface ExtractionMethodBadgeProps {
  extractionMethod?: string;
  usedClaude?: boolean;
  learningImprovements?: number;
  templateName?: string;
  size?: 'xs' | 'sm' | 'md';
  showAll?: boolean;  // Show all relevant badges
}

/**
 * ExtractionMethodBadge - Reusable badge component for extraction methods
 *
 * Displays visual indicators for:
 * - Hybrid extraction
 * - AI validation
 * - Template matching
 * - Learning improvements
 */
const ExtractionMethodBadge: React.FC<ExtractionMethodBadgeProps> = ({
  extractionMethod,
  usedClaude,
  learningImprovements,
  templateName,
  size = 'sm',
  showAll = false
}) => {
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-[10px]',
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm'
  };

  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-4 h-4'
  };

  const baseClass = `inline-flex items-center rounded-lg font-medium border ${sizeClasses[size]}`;
  const iconClass = `${iconSizes[size]} mr-1`;

  const badges = [];

  // Hybrid Extraction Badge
  if (extractionMethod === 'hybrid') {
    badges.push(
      <span
        key="hybrid"
        className={`${baseClass} bg-blue-500/20 text-blue-300 border-blue-500/30`}
        title="3-layer hybrid extraction: Position-aware → Learning boost → AI validation"
      >
        <Zap className={iconClass} />
        Hybrid Extraction
      </span>
    );
  }

  // Template Badge
  if (extractionMethod === 'template' && templateName) {
    badges.push(
      <span
        key="template"
        className={`${baseClass} bg-green-500/20 text-green-300 border-green-500/30`}
        title={`Extracted using template: ${templateName}`}
      >
        <FileText className={iconClass} />
        Template: {templateName}
      </span>
    );
  }

  // AI Validation Badge
  if (usedClaude && (showAll || extractionMethod === 'hybrid')) {
    badges.push(
      <span
        key="ai"
        className={`${baseClass} bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30`}
        title="AI validated for improved accuracy"
      >
        <Sparkles className={iconClass} />
        AI Validated
      </span>
    );
  }

  // Learning Improvements Badge
  if (learningImprovements && learningImprovements > 0) {
    badges.push(
      <span
        key="learning"
        className={`${baseClass} bg-green-500/20 text-green-300 border-green-500/30`}
        title={`${learningImprovements} fields improved by learning system`}
      >
        <TrendingUp className={iconClass} />
        +{learningImprovements} Learning Boost
      </span>
    );
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {badges}
    </div>
  );
};

export default ExtractionMethodBadge;
